import { PageSize, SortDirection } from 'gf-types';
import { NavigateFunction } from 'react-router-dom';

export type GenericUrlFilters = Record<string, string | number | boolean | Date | ExplicitAny>;

export type DefaultListFilters = {
  search?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortDir?: SortDirection;
  page?: number;
  pageSize?: PageSize;
  folderId?: string;
} & GenericUrlFilters;

export type UrlFilteringOptions = {
  filterDefaults?: GenericUrlFilters;
};

/** The common default list filters that we use throughout the app */
export const filterDefaults: DefaultListFilters = {
  page: 1,
  pageSize: 25
};

export const listFilterDefaultKeys = ['search', 'page', 'pageSize', 'startDate', 'endDate', 'sortBy', 'sortDir', 'folderId'];

/**
 * Gets the standard set of filters commonly used for filtering lists in our app.
 * This function works with all `'key=value&foo=bar'` strings.
 *
 * @param query The query string or `URLSearchParams` object. Can include the leading `?` if passing in a string.
 */
export function getListFilters(query: string | URLSearchParams, additionalFilterKeys: string[] = []): DefaultListFilters {
  // this list should match the `ListFilters` type above
  const listFilterKeys = [...listFilterDefaultKeys, ...additionalFilterKeys];
  const searchParams = new URLSearchParams(query);
  const filters: DefaultListFilters = { ...filterDefaults };

  searchParams.forEach((value, key) => {
    const filterKey = listFilterKeys.find((fk) => fk.toLowerCase() === key.toLowerCase());

    if (!filterKey) {
      return;
    }

    switch (filterKey) {
      case 'page':
        filters[filterKey] = parseInt(value) || 1;
        break;
      case 'pageSize':
        filters[filterKey] = (parseInt(value) as PageSize) || 25;
        break;
      default:
        filters[filterKey] = value;
        break;
    }
  });

  return filters;
}

/**
 * Updates the browers URL with the given `pathname` and `filters`.
 *
 * NOTE: This calls `navigate(..., { replace: true })` with the `pathname` and `filters`.
 *       Duplicate keys will be overwritten.
 *
 * @param navigate The navigate function.
 * @param pathname The pathname to set on the history.
 * @param filters The query string filters.
 * @param additionalParams Additional query string params to set.
 */
export function updateListFiltersUrl(navigate: NavigateFunction, pathname: string, filters: DefaultListFilters, additionalParams?: { [name: string]: string }) {
  const searchParams = new URLSearchParams();
  const combinedFilters = { ...filters, ...additionalParams };

  for (const key in combinedFilters) {
    const param = combinedFilters[key];

    if (!!param) {
      searchParams.set(key, param.toString());
    }
  }

  // replace the current path with the given path and query
  navigate(getNavigateUrlWithParams(pathname, searchParams), { replace: true });
}

export function getNavigateUrlWithParams(pathname: string, params: URLSearchParams) {
  const queryString = params.toString();
  return `/${pathname}${queryString ? '?' : ''}${queryString}`;
}

/**
 * Checks to see if the given string value contains any url-like tokens in it.
 *
 * @param value The embedded value to check for a url.
 */
export function hasUrlEmbedded(value: string): boolean {
  //removing a set of characters that we believe are not visible so that our regex can pick up bad urls
  const sanitizedValue = value?.replace(/[^\x00-\x7F]/, '');
  // Check if the first character is an equals sign for calculations
  if (sanitizedValue?.startsWith('=')) {
    return false;
  }
  return /(?:(?:[a-zA-Z]+:\/\/){0,1}[a-zA-Z0-9\-\.]*[a-zA-Z0-9]\.[a-zA-Z]{2,}(?:\/[^\s""']*)*(?=$|\s)*|(?:https:\/\/|http:\/\/|:\/\/))/gim.test(sanitizedValue);
}

/**
 * Checks to see if the given string value contains any non goformz.com url-like tokens in it.
 *
 * @param value The embedded value to check for a non GoFormz url.
 */
export function hasNonGoFormzUrlEmbedded(value: string): boolean {
  // Check if the first character is an equals sign for calculations
  if (value?.startsWith('=')) {
    return false;
  }
  // Remove all matches of URLs like https://*.goformz.com where * is any letter a-z
  // Also removing a set of characters that we believe are not visible so that our regex can pick up bad urls
  const sanitizedValue = value?.replace(/https:\/\/[a-zA-Z]+\.goformz\.com/g, '')?.replace(/[^\x00-\x7F]/, '');
  return /(?:(?:[a-zA-Z]+:\/\/){0,1}[a-zA-Z0-9\-\.]*[a-zA-Z0-9]\.[a-zA-Z]{2,}(?:\/[^\s""']*)*(?=$|\s)*|(?:https:\/\/|http:\/\/|:\/\/))/gim.test(sanitizedValue);
}

/**
 * Creates a path with query string where all of the query param keys are lowercased.
 *
 * @param path The path of the url.
 * @param filters The search filters to create.
 * @param options The options to apple to the filters.
 *
 * @example
 * const filters = { page: 1, pageSize: 10, sortDir: 'desc' };
 * const filterDefaults = { page: 1, pageSize: 25, sortDir: 'desc' };
 *
 * // returns: 'some/path?pagesize=25'
 * const url = createPathSearchUrl('some/path', filters, { filterDefaults });
 */
export function createPathSearchUrl(path: string, filters: DefaultListFilters, options: UrlFilteringOptions = { filterDefaults: {} }): string {
  const { filterDefaults } = options;
  const search = new URLSearchParams();

  // build the search params
  Object.entries(filters)
    // filter out any filters that are in the default options with the same value
    .filter(([filterKey, filterValue]) => {
      // check to make sure a case-insensitive version was given
      const defaultValue = filterDefaults[filterKey] ?? filterDefaults[filterKey.toLowerCase()];
      // has to have a valid value and the value can't be the default value
      const invalidValue = filterValue === null || filterValue === undefined;

      return !invalidValue && filterValue !== defaultValue;
    })
    // build a lowercased, encoded, url search query using
    // the built-in URLSearchParams class
    .forEach(([key, value]) => {
      search.append(key, value.toString());
    });

  // return path + search query together
  const query = search.toString();
  const fullPath = `${path}${query ? `?${query}` : ''}`;

  return fullPath.toLowerCase();
}

/**
 * Parses the querystring and return a URLSearchParams object containing all of the
 * key/value pairs.
 *
 * Use [getQuerystringParameters](../helpers/utility.ts) for legacy parsing.
 *
 * @param search The query string.
 * @param lowercase True, to turn all of the keys into lowercase versions, false otherwise.
 */
export function getSearchParams(search: string, lowercase = false): URLSearchParams {
  const params = new URLSearchParams(search);

  if (!lowercase || params.size === 0) {
    return params;
  }

  const remappedParams = Array.from(params.entries()).map(([key, value]) => [key.toLowerCase(), value]);
  const loweredParams = new URLSearchParams(remappedParams);

  return loweredParams;
}
